// The create-an-agent wizard.
//
// Four steps, because the form is genuinely long and a single page of it reads
// as work. The split is by what the customer is thinking about, not by what the
// backend needs: who the bot is, what it knows, how it behaves, where it lives.
//
// It runs signed out as well as signed in. A stranger can fill in all four
// steps and only meet the signup form when they press the button at the end --
// see StartWizard in App.tsx for the route that serves them. Nothing before
// that button costs us anything, because nothing before it talks to the server:
// every answer is local state until submit(), with the deliberate exception of
// uploaded files, which need somewhere to live and are therefore the one thing
// KnowledgeEditor disables until there is an account.
//
// Every keystroke is mirrored into sessionStorage by lib/draft. That is not a
// convenience: signing up unmounts this component and remounts it on a
// different route, so without the draft the reward for filling in the wizard
// would be an empty wizard.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as api from "../lib/api";
import { ApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STEPS, clearDraft, loadDraft, saveDraft } from "../lib/draft";
import { validateSources } from "../lib/knowledge";
import {
  FREE_AGENT_LIMIT,
  FREE_ANSWERS_PER_MONTH,
  FREE_QUESTIONS_PER_SESSION,
  PAID_LIMIT_CHOICES,
  limitLabel
} from "../lib/plans";
import { DEFAULT_THEME } from "../lib/theme";
import type { AgentTheme, KnowledgeSource } from "../lib/types";
import AuthForm from "../components/AuthForm";
import KnowledgeEditor from "../components/KnowledgeEditor";
import { reportBotBuilt } from "../lib/ads";
import { track } from "../lib/analytics";
import ThemeEditor from "../components/ThemeEditor";
import { Button, Card, ErrorBanner, Field, Notice, PageHeading, Select, Spinner, TextArea, TextInput } from "../components/ui";


const TONES = [
  "Friendly and conversational",
  "Warm but concise",
  "Professional and formal",
  "Enthusiastic and upbeat",
  "Plain and factual"
];

export default function NewAgent() {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();

  // Read once, on mount. Reading it on every render would fight the state it
  // seeds, and reading it later would resurrect answers the customer has since
  // deleted.
  const [saved] = useState(loadDraft);

  const [step, setStep] = useState(saved?.step ?? 0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<string[]>([]);

  const [name, setName] = useState(saved?.name ?? "");
  const [businessName, setBusinessName] = useState(saved?.businessName ?? "");
  const [description, setDescription] = useState(saved?.description ?? "");
  const [tone, setTone] = useState(saved?.tone ?? TONES[0]);
  const [customInstructions, setCustomInstructions] = useState(saved?.customInstructions ?? "");
  const [sources, setSources] = useState<KnowledgeSource[]>(
    saved?.sources ?? [{ type: "qa", pairs: [{ question: "", answer: "" }] }]
  );
  const [questionsPerSession, setQuestionsPerSession] = useState<number | null>(
    saved ? saved.questionsPerSession : 20
  );
  const [theme, setTheme] = useState<AgentTheme>(saved?.theme ?? DEFAULT_THEME);
  const [domainsText, setDomainsText] = useState(saved?.domainsText ?? "");

  // Pressed "Create agent" while signed out. Kept in the draft rather than in
  // component state alone, because the sign-up that follows is exactly what
  // destroys component state.
  const [submitWhenReady, setSubmitWhenReady] = useState(saved?.submitWhenReady ?? false);

  const allowedOrigins = useMemo(
    () =>
      domainsText
        .split(/[\s,]+/)
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean),
    [domainsText]
  );

  const sourceCheck = useMemo(() => validateSources(sources), [sources]);

  // Each step knows only whether *it* is complete. The server validates the
  // whole thing again on submit; this only decides whether Next is clickable.
  const stepComplete = [
    Boolean(name.trim() && businessName.trim()),
    sourceCheck.ok,
    true,
    true
  ];

  useEffect(() => {
    saveDraft({
      step,
      name,
      businessName,
      description,
      tone,
      customInstructions,
      sources,
      questionsPerSession,
      theme,
      domainsText,
      submitWhenReady
    });
  }, [
    step,
    name,
    businessName,
    description,
    tone,
    customInstructions,
    sources,
    questionsPerSession,
    theme,
    domainsText,
    submitWhenReady
  ]);

  // How many agents this account can still create without paying. null while we
  // do not know -- either because nobody is signed in yet, or because the call
  // is in flight or failed. The distinction matters: an unknown count must not
  // block the button, because the real limit lives on the server and a warning
  // that fires on a failed request would refuse people who are well within it.
  const [freeSlotsLeft, setFreeSlotsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!tenant) return;
    let canceled = false;
    api
      .listAgents(tenant.tenantId)
      .then(({ agents }) => {
        if (canceled) return;
        // Archived agents are in this list and are counted on purpose: see
        // plans.occupiesFreeSlot on the server. Archiving keeps the bot's
        // knowledge and passages, so it frees nothing.
        const used = agents.filter((agent) => !agent.paid).length;
        setFreeSlotsLeft(Math.max(0, FREE_AGENT_LIMIT - used));
      })
      .catch(() => {
        // Deliberately silent. This is a courtesy warning, not the enforcement.
      });
    return () => {
      canceled = true;
    };
  }, [tenant]);

  const outOfFreeSlots = freeSlotsLeft === 0;

  // Fires at most once per mount, including the mount that happens right after
  // signing up. Without the ref, a re-render between the tenant arriving and
  // submitting turning true would start a second provisioning run, and the
  // customer would pay for two agents they asked for once.
  const autoSubmitted = useRef(false);

  useEffect(() => {
    if (!submitWhenReady || !tenant || autoSubmitted.current) return;
    autoSubmitted.current = true;
    void submit();
    // submit is recreated every render and is not worth memoizing for this; the
    // ref above is what actually keeps this to one call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitWhenReady, tenant]);

  async function submit() {
    if (!tenant) {
      // Signed out: interrupt for an account and come back. The draft is
      // already written, so the answers survive the remount that signing up
      // causes.
      setSubmitWhenReady(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    setDetails([]);
    try {
      const created = await api.createAgent({
        tenantId: tenant.tenantId,
        name: name.trim(),
        businessName: businessName.trim(),
        description,
        tone,
        customInstructions,
        sources,
        limits: { questionsPerSession },
        theme,
        allowedOrigins
      });
      // Only now. Clearing earlier would lose the wizard if the request failed,
      // which is the one moment the customer most needs it kept.
      clearDraft();
      // Tells Google Ads a bot was built, if an ad brought them (lib/ads.ts).
      reportBotBuilt({ agentId: created.agentId, email: user?.email ?? null });
      track("bot_built");
      // Straight to the detail page, which is also the progress page: the
      // pipeline has already started by the time this resolves.
      navigate(`/agents/${created.agentId}`, { replace: true });
    } catch (err) {
      setError((err as Error).message);
      if (err instanceof ApiError) setDetails(err.fieldErrors);
      setSubmitting(false);
      // Let them try again, or sign in as somebody else, rather than looping.
      setSubmitWhenReady(false);
      autoSubmitted.current = false;
    }
  }

  // The wizard is done and the only thing missing is an account. Two flavors:
  // no Firebase user at all (show the sign-up form), or a brand-new user whose
  // first tenant is still being created a request or two behind them.
  if (submitWhenReady && !tenant) {
    return (
      <>
        <PageHeading
          title="Almost there"
          subtitle="Create your account and we'll build the agent you just described."
        />
        <ErrorBanner message={error} details={details} />
        <Card>
          {user ? (
            <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
              <Spinner /> Setting up your workspace…
            </div>
          ) : (
            <>
              <AuthForm
                initialMode="signup"
                headingAs="h2"
                signupCta="Create account & build agent"
              />
              <div className="mt-6 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={() => setSubmitWhenReady(false)}
                  className="text-sm text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline"
                >
                  Back to the wizard
                </button>
                <p className="mt-2 text-xs text-slate-400">
                  Your answers are saved in this tab, so signing up will not lose them.
                </p>
              </div>
            </>
          )}
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeading
        title="New agent"
        subtitle={
          tenant
            ? "Four short steps. Setup starts as soon as you finish."
            : "Four short steps. You only need an account at the very end."
        }
      />

      {!user && (
        <p className="-mt-2 mb-6 text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/signin" className="font-medium text-brand-700 hover:text-brand-800">
            Sign in
          </Link>
          . Your answers here are kept either way.
        </p>
      )}

      <ol className="mb-6 flex flex-wrap gap-2 text-sm" aria-label="Progress">
        {STEPS.map((label, index) => (
          <li key={label}>
            <button
              type="button"
              // Backward only. Jumping ahead past an incomplete step would let
              // someone submit a wizard they have not filled in.
              disabled={index > step}
              onClick={() => setStep(index)}
              className={
                "rounded-full px-3 py-1 transition disabled:cursor-not-allowed " +
                (index === step
                  ? "bg-brand-600 text-white"
                  : index < step
                    ? "bg-brand-50 text-brand-700 hover:bg-brand-100"
                    : "bg-slate-100 text-slate-400")
              }
            >
              {index + 1}. {label}
            </button>
          </li>
        ))}
      </ol>

      <ErrorBanner message={error} details={details} />

      <Card>
        {step === 0 && (
          <div className="space-y-5">
            <Field label="Agent name" hint="Just for you. Visitors never see it.">
              <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Support bot" />
            </Field>
            <Field label="Business name" hint="How the assistant introduces itself.">
              <TextInput
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Acme Coffee Roasters"
              />
            </Field>
            <Field label="What does your business do?" hint="A sentence or two. Helps the bot answer in context.">
              <TextArea
                rows={3}
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="We roast and ship single-origin coffee to customers across the US."
              />
            </Field>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-medium text-slate-900">What should it know?</h2>
              <p className="mt-1 text-sm text-slate-500">
                Everything you add here is indexed and used to answer questions. The bot answers from
                this and nothing else.
              </p>
            </div>
            <KnowledgeEditor
              tenantId={tenant ? tenant.tenantId : null}
              sources={sources}
              onChange={setSources}
            />
            {!sourceCheck.ok && sources.length > 0 && (
              <ErrorBanner message="Some sources need fixing before you can continue." details={sourceCheck.errors} />
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <Field label="Tone of voice">
              <Select value={tone} onChange={(e) => setTone(e.target.value)}>
                {TONES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Anything else it should always do?"
              hint="Optional. For example: always offer the phone number if you can't answer."
            >
              <TextArea
                rows={3}
                maxLength={1000}
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
              />
            </Field>

            {/* Every new bot starts free, so this is the ceiling for later, not
                now. Saying "once upgraded" in the label itself is what stops
                somebody picking 100 and expecting 100 on a free bot. */}
            <Field
              label="Questions per visitor session, once upgraded"
              hint={`Until you upgrade, this bot answers up to ${FREE_ANSWERS_PER_MONTH} questions a month across all your visitors, and ${FREE_QUESTIONS_PER_SESSION} in any one conversation. Your choice here takes effect the moment you upgrade.`}
            >
              <Select
                value={questionsPerSession === null ? "unlimited" : String(questionsPerSession)}
                onChange={(e) =>
                  setQuestionsPerSession(e.target.value === "unlimited" ? null : Number(e.target.value))
                }
              >
                {PAID_LIMIT_CHOICES.map((choice) => (
                  <option key={String(choice)} value={choice === null ? "unlimited" : String(choice)}>
                    {limitLabel(choice)}
                  </option>
                ))}
              </Select>
            </Field>

            <div>
              <h3 className="mb-3 text-base font-medium text-slate-900">Appearance</h3>
              <ThemeEditor theme={theme} onChange={setTheme} paid={false} />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <Field
              label="Which websites may embed this agent?"
              hint="One per line. Leave blank to allow any site. That's fine while you're testing, but worth locking down before launch."
            >
              <TextArea
                rows={4}
                spellCheck={false}
                value={domainsText}
                onChange={(e) => setDomainsText(e.target.value)}
                placeholder={"example.com\nwww.example.com"}
              />
            </Field>

            {allowedOrigins.length === 0 && (
              <Notice tone="warning">
                With no domains listed, anyone who finds your agent's id can embed it on their own
                site and it will answer.
              </Notice>
            )}

            {outOfFreeSlots ? (
              <Notice tone="warning">
                <p className="font-medium">You've used your free agent</p>
                <p className="mt-1">
                  A free account has {FREE_AGENT_LIMIT === 1 ? "one free agent" : `${FREE_AGENT_LIMIT} free agents`}. Upgrade your existing agent on the{" "}
                  <Link to="/billing" className="font-medium underline">
                    billing page
                  </Link>{" "}
                  to make room for this one, or delete it. Archiving does not free the slot, because an archived agent
                  keeps what it learned so that restoring it doesn't mean adding it all again.
                </p>
              </Notice>
            ) : (
              <Notice>
                <p className="font-medium">Ready to build</p>
                <p className="mt-1">
                  We'll build your assistant from {sources.length} source
                  {sources.length === 1 ? "" : "s"}, then hand you a snippet for your website, a
                  short link and a QR code to put on cards, signs and email signatures. Reading a
                  website can take a few minutes. You can leave the page while it runs.
                </p>
                {!user && (
                  <p className="mt-2">
                    Pressing the button asks you to create an account first. Nothing you've typed is
                    lost.
                  </p>
                )}
                {freeSlotsLeft !== null && (
                  <p className="mt-2 text-slate-500">
                    {FREE_AGENT_LIMIT === 1
                      ? "This will be your free agent."
                      : `${freeSlotsLeft} of your ${FREE_AGENT_LIMIT} free agents ${freeSlotsLeft === 1 ? "is" : "are"} still available.`}
                  </p>
                )}
              </Notice>
            )}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-5">
          <Button
            type="button"
            variant="ghost"
            onClick={() => (step === 0 ? navigate(user ? "/agents" : "/") : setStep(step - 1))}
          >
            {step === 0 ? "Cancel" : "Back"}
          </Button>

          {step < STEPS.length - 1 ? (
            <Button type="button" onClick={() => setStep(step + 1)} disabled={!stepComplete[step]}>
              Next
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void submit()}
              busy={submitting}
              disabled={outOfFreeSlots}
            >
              {user ? "Create agent" : "Create account & build"}
            </Button>
          )}
        </div>
      </Card>
    </>
  );
}
